// Curated, dependency-free emoji dataset for the chat composer picker.
//
// Intentionally a popular subset (~300 emojis), NOT the full unicode set —
// keeps the bundle small (just data) and the picker fast. Each entry carries
// `keywords` so `searchEmojis` can match on intent ("happy", "thumbs", "party")
// as well as on the canonical name.
//
// Shape:
//   EMOJI_CATEGORIES = [{ id, label, emojis: [{ char, name, keywords:[...] }] }]

export const EMOJI_CATEGORIES = [
  {
    id: 'smileys',
    label: 'Smileys & People',
    emojis: [
      { char: '😀', name: 'grinning face', keywords: ['happy', 'smile', 'grin'] },
      { char: '😃', name: 'grinning face with big eyes', keywords: ['happy', 'smile', 'joy'] },
      { char: '😄', name: 'grinning face with smiling eyes', keywords: ['happy', 'smile', 'laugh'] },
      { char: '😁', name: 'beaming face with smiling eyes', keywords: ['happy', 'grin'] },
      { char: '😆', name: 'grinning squinting face', keywords: ['laugh', 'haha', 'lol'] },
      { char: '😅', name: 'grinning face with sweat', keywords: ['phew', 'relief', 'laugh'] },
      { char: '🤣', name: 'rolling on the floor laughing', keywords: ['rofl', 'lol', 'funny'] },
      { char: '😂', name: 'face with tears of joy', keywords: ['lol', 'laugh', 'cry', 'funny'] },
      { char: '🙂', name: 'slightly smiling face', keywords: ['smile', 'happy'] },
      { char: '🙃', name: 'upside-down face', keywords: ['silly', 'sarcasm'] },
      { char: '😉', name: 'winking face', keywords: ['wink', 'flirt'] },
      { char: '😊', name: 'smiling face with smiling eyes', keywords: ['happy', 'blush', 'smile'] },
      { char: '😇', name: 'smiling face with halo', keywords: ['angel', 'innocent'] },
      { char: '🥰', name: 'smiling face with hearts', keywords: ['love', 'adore', 'crush'] },
      { char: '😍', name: 'smiling face with heart-eyes', keywords: ['love', 'crush', 'heart'] },
      { char: '🤩', name: 'star-struck', keywords: ['wow', 'amazing', 'stars'] },
      { char: '😘', name: 'face blowing a kiss', keywords: ['kiss', 'love'] },
      { char: '😗', name: 'kissing face', keywords: ['kiss'] },
      { char: '😋', name: 'face savoring food', keywords: ['yum', 'tasty', 'delicious'] },
      { char: '😛', name: 'face with tongue', keywords: ['tongue', 'silly'] },
      { char: '😜', name: 'winking face with tongue', keywords: ['silly', 'crazy', 'wink'] },
      { char: '🤪', name: 'zany face', keywords: ['crazy', 'silly', 'wild'] },
      { char: '😝', name: 'squinting face with tongue', keywords: ['silly', 'tongue'] },
      { char: '🤑', name: 'money-mouth face', keywords: ['money', 'rich', 'dollar'] },
      { char: '🤗', name: 'hugging face', keywords: ['hug', 'embrace'] },
      { char: '🤭', name: 'face with hand over mouth', keywords: ['oops', 'giggle', 'shy'] },
      { char: '🤫', name: 'shushing face', keywords: ['quiet', 'shh', 'secret'] },
      { char: '🤔', name: 'thinking face', keywords: ['think', 'hmm', 'consider'] },
      { char: '🤐', name: 'zipper-mouth face', keywords: ['quiet', 'secret', 'silence'] },
      { char: '😐', name: 'neutral face', keywords: ['meh', 'blank'] },
      { char: '😑', name: 'expressionless face', keywords: ['blank', 'meh'] },
      { char: '😶', name: 'face without mouth', keywords: ['speechless', 'silent'] },
      { char: '😏', name: 'smirking face', keywords: ['smirk', 'smug'] },
      { char: '😒', name: 'unamused face', keywords: ['meh', 'annoyed', 'unimpressed'] },
      { char: '🙄', name: 'face with rolling eyes', keywords: ['eyeroll', 'whatever'] },
      { char: '😬', name: 'grimacing face', keywords: ['awkward', 'yikes'] },
      { char: '🤥', name: 'lying face', keywords: ['lie', 'pinocchio'] },
      { char: '😌', name: 'relieved face', keywords: ['relief', 'calm'] },
      { char: '😔', name: 'pensive face', keywords: ['sad', 'thoughtful'] },
      { char: '😪', name: 'sleepy face', keywords: ['tired', 'sleepy'] },
      { char: '😴', name: 'sleeping face', keywords: ['sleep', 'zzz', 'tired'] },
      { char: '😷', name: 'face with medical mask', keywords: ['sick', 'mask', 'ill'] },
      { char: '🤒', name: 'face with thermometer', keywords: ['sick', 'fever', 'ill'] },
      { char: '🤕', name: 'face with head-bandage', keywords: ['hurt', 'injured'] },
      { char: '🤢', name: 'nauseated face', keywords: ['sick', 'gross', 'ill'] },
      { char: '🤮', name: 'face vomiting', keywords: ['sick', 'gross', 'vomit'] },
      { char: '🤧', name: 'sneezing face', keywords: ['sneeze', 'sick'] },
      { char: '🥵', name: 'hot face', keywords: ['hot', 'heat', 'sweat'] },
      { char: '🥶', name: 'cold face', keywords: ['cold', 'freezing'] },
      { char: '🥴', name: 'woozy face', keywords: ['drunk', 'dizzy'] },
      { char: '😵', name: 'dizzy face', keywords: ['dizzy', 'confused'] },
      { char: '🤯', name: 'exploding head', keywords: ['mind blown', 'shocked', 'wow'] },
      { char: '🤠', name: 'cowboy hat face', keywords: ['cowboy', 'yeehaw'] },
      { char: '🥳', name: 'partying face', keywords: ['party', 'celebrate', 'birthday'] },
      { char: '😎', name: 'smiling face with sunglasses', keywords: ['cool', 'sunglasses'] },
      { char: '🤓', name: 'nerd face', keywords: ['nerd', 'geek', 'smart'] },
      { char: '🧐', name: 'face with monocle', keywords: ['inspect', 'fancy', 'hmm'] },
      { char: '😕', name: 'confused face', keywords: ['confused', 'unsure'] },
      { char: '😟', name: 'worried face', keywords: ['worried', 'concern'] },
      { char: '🙁', name: 'slightly frowning face', keywords: ['sad', 'frown'] },
      { char: '😮', name: 'face with open mouth', keywords: ['wow', 'surprise', 'shock'] },
      { char: '😯', name: 'hushed face', keywords: ['surprise', 'quiet'] },
      { char: '😲', name: 'astonished face', keywords: ['shock', 'wow', 'gasp'] },
      { char: '😳', name: 'flushed face', keywords: ['blush', 'embarrassed'] },
      { char: '🥺', name: 'pleading face', keywords: ['please', 'puppy eyes', 'beg'] },
      { char: '😦', name: 'frowning face with open mouth', keywords: ['sad', 'shock'] },
      { char: '😨', name: 'fearful face', keywords: ['scared', 'fear'] },
      { char: '😰', name: 'anxious face with sweat', keywords: ['anxious', 'nervous'] },
      { char: '😥', name: 'sad but relieved face', keywords: ['sad', 'relief'] },
      { char: '😢', name: 'crying face', keywords: ['cry', 'sad', 'tear'] },
      { char: '😭', name: 'loudly crying face', keywords: ['cry', 'sob', 'sad'] },
      { char: '😱', name: 'face screaming in fear', keywords: ['scream', 'shock', 'scared'] },
      { char: '😖', name: 'confounded face', keywords: ['frustrated', 'ugh'] },
      { char: '😣', name: 'persevering face', keywords: ['struggle', 'effort'] },
      { char: '😞', name: 'disappointed face', keywords: ['sad', 'disappointed'] },
      { char: '😓', name: 'downcast face with sweat', keywords: ['sad', 'sweat'] },
      { char: '😩', name: 'weary face', keywords: ['tired', 'ugh', 'weary'] },
      { char: '😫', name: 'tired face', keywords: ['tired', 'exhausted'] },
      { char: '🥱', name: 'yawning face', keywords: ['tired', 'bored', 'yawn'] },
      { char: '😤', name: 'face with steam from nose', keywords: ['frustrated', 'angry', 'mad'] },
      { char: '😡', name: 'enraged face', keywords: ['angry', 'mad', 'rage'] },
      { char: '😠', name: 'angry face', keywords: ['angry', 'mad'] },
      { char: '🤬', name: 'face with symbols on mouth', keywords: ['cursing', 'swear', 'angry'] },
      { char: '😈', name: 'smiling face with horns', keywords: ['devil', 'evil', 'mischief'] },
      { char: '💀', name: 'skull', keywords: ['dead', 'skull', 'death'] },
      { char: '💩', name: 'pile of poo', keywords: ['poop', 'crap'] },
      { char: '🤡', name: 'clown face', keywords: ['clown', 'joke'] },
      { char: '👻', name: 'ghost', keywords: ['ghost', 'boo', 'halloween'] },
      { char: '👽', name: 'alien', keywords: ['alien', 'ufo', 'space'] },
      { char: '🤖', name: 'robot', keywords: ['robot', 'bot', 'ai'] },
      { char: '🎃', name: 'jack-o-lantern', keywords: ['halloween', 'pumpkin'] },
    ],
  },
  {
    id: 'gestures',
    label: 'Gestures & Body',
    emojis: [
      { char: '👍', name: 'thumbs up', keywords: ['like', 'approve', 'yes', 'good'] },
      { char: '👎', name: 'thumbs down', keywords: ['dislike', 'no', 'bad'] },
      { char: '👌', name: 'OK hand', keywords: ['ok', 'okay', 'perfect'] },
      { char: '🤌', name: 'pinched fingers', keywords: ['italian', 'gesture'] },
      { char: '🤏', name: 'pinching hand', keywords: ['small', 'tiny'] },
      { char: '✌️', name: 'victory hand', keywords: ['peace', 'victory'] },
      { char: '🤞', name: 'crossed fingers', keywords: ['luck', 'hope', 'wish'] },
      { char: '🤟', name: 'love-you gesture', keywords: ['love', 'rock'] },
      { char: '🤘', name: 'sign of the horns', keywords: ['rock', 'metal'] },
      { char: '🤙', name: 'call me hand', keywords: ['call', 'shaka', 'hang loose'] },
      { char: '👈', name: 'backhand index pointing left', keywords: ['point', 'left'] },
      { char: '👉', name: 'backhand index pointing right', keywords: ['point', 'right'] },
      { char: '👆', name: 'backhand index pointing up', keywords: ['point', 'up'] },
      { char: '👇', name: 'backhand index pointing down', keywords: ['point', 'down'] },
      { char: '☝️', name: 'index pointing up', keywords: ['point', 'one', 'up'] },
      { char: '✋', name: 'raised hand', keywords: ['stop', 'high five', 'hand'] },
      { char: '🤚', name: 'raised back of hand', keywords: ['hand', 'stop'] },
      { char: '🖐️', name: 'hand with fingers splayed', keywords: ['hand', 'five'] },
      { char: '🖖', name: 'vulcan salute', keywords: ['spock', 'star trek'] },
      { char: '👋', name: 'waving hand', keywords: ['wave', 'hello', 'hi', 'bye'] },
      { char: '🤝', name: 'handshake', keywords: ['deal', 'agree', 'shake'] },
      { char: '👏', name: 'clapping hands', keywords: ['clap', 'applause', 'bravo'] },
      { char: '🙌', name: 'raising hands', keywords: ['celebrate', 'praise', 'hooray'] },
      { char: '👐', name: 'open hands', keywords: ['open', 'hug'] },
      { char: '🙏', name: 'folded hands', keywords: ['pray', 'thanks', 'please', 'hope'] },
      { char: '✊', name: 'raised fist', keywords: ['fist', 'power', 'solidarity'] },
      { char: '👊', name: 'oncoming fist', keywords: ['fist bump', 'punch'] },
      { char: '🤛', name: 'left-facing fist', keywords: ['fist bump'] },
      { char: '🤜', name: 'right-facing fist', keywords: ['fist bump'] },
      { char: '💪', name: 'flexed biceps', keywords: ['strong', 'muscle', 'gym'] },
      { char: '🦾', name: 'mechanical arm', keywords: ['robot', 'strong'] },
      { char: '🖕', name: 'middle finger', keywords: ['rude', 'flip'] },
      { char: '✍️', name: 'writing hand', keywords: ['write', 'pen'] },
      { char: '🤳', name: 'selfie', keywords: ['selfie', 'photo'] },
      { char: '💅', name: 'nail polish', keywords: ['nails', 'manicure', 'sass'] },
      { char: '👀', name: 'eyes', keywords: ['look', 'watch', 'see'] },
      { char: '👁️', name: 'eye', keywords: ['eye', 'look'] },
      { char: '👅', name: 'tongue', keywords: ['tongue', 'lick'] },
      { char: '👄', name: 'mouth', keywords: ['lips', 'kiss'] },
      { char: '🧠', name: 'brain', keywords: ['brain', 'smart', 'think'] },
      { char: '🦴', name: 'bone', keywords: ['bone'] },
      { char: '👂', name: 'ear', keywords: ['ear', 'hear', 'listen'] },
      { char: '👃', name: 'nose', keywords: ['nose', 'smell'] },
    ],
  },
  {
    id: 'hearts',
    label: 'Hearts & Emotion',
    emojis: [
      { char: '❤️', name: 'red heart', keywords: ['love', 'heart', 'like'] },
      { char: '🧡', name: 'orange heart', keywords: ['love', 'heart'] },
      { char: '💛', name: 'yellow heart', keywords: ['love', 'heart'] },
      { char: '💚', name: 'green heart', keywords: ['love', 'heart'] },
      { char: '💙', name: 'blue heart', keywords: ['love', 'heart'] },
      { char: '💜', name: 'purple heart', keywords: ['love', 'heart'] },
      { char: '🖤', name: 'black heart', keywords: ['love', 'heart', 'dark'] },
      { char: '🤍', name: 'white heart', keywords: ['love', 'heart'] },
      { char: '🤎', name: 'brown heart', keywords: ['love', 'heart'] },
      { char: '💔', name: 'broken heart', keywords: ['heartbreak', 'sad', 'breakup'] },
      { char: '❣️', name: 'heart exclamation', keywords: ['love', 'heart'] },
      { char: '💕', name: 'two hearts', keywords: ['love', 'hearts'] },
      { char: '💞', name: 'revolving hearts', keywords: ['love', 'hearts'] },
      { char: '💓', name: 'beating heart', keywords: ['love', 'heartbeat'] },
      { char: '💗', name: 'growing heart', keywords: ['love', 'heart'] },
      { char: '💖', name: 'sparkling heart', keywords: ['love', 'sparkle', 'heart'] },
      { char: '💘', name: 'heart with arrow', keywords: ['cupid', 'love'] },
      { char: '💝', name: 'heart with ribbon', keywords: ['gift', 'love'] },
      { char: '💋', name: 'kiss mark', keywords: ['kiss', 'lips', 'love'] },
      { char: '💯', name: 'hundred points', keywords: ['100', 'perfect', 'score'] },
      { char: '💢', name: 'anger symbol', keywords: ['angry', 'mad'] },
      { char: '💥', name: 'collision', keywords: ['boom', 'explosion'] },
      { char: '💫', name: 'dizzy', keywords: ['stars', 'dizzy'] },
      { char: '💦', name: 'sweat droplets', keywords: ['sweat', 'water'] },
      { char: '💨', name: 'dashing away', keywords: ['fast', 'wind', 'gone'] },
      { char: '🕳️', name: 'hole', keywords: ['hole'] },
      { char: '💬', name: 'speech balloon', keywords: ['talk', 'comment', 'chat'] },
      { char: '💭', name: 'thought balloon', keywords: ['think', 'thought'] },
      { char: '💤', name: 'zzz', keywords: ['sleep', 'tired', 'zzz'] },
    ],
  },
  {
    id: 'animals',
    label: 'Animals & Nature',
    emojis: [
      { char: '🐶', name: 'dog face', keywords: ['dog', 'puppy', 'pet'] },
      { char: '🐱', name: 'cat face', keywords: ['cat', 'kitten', 'pet'] },
      { char: '🐭', name: 'mouse face', keywords: ['mouse'] },
      { char: '🐹', name: 'hamster', keywords: ['hamster', 'pet'] },
      { char: '🐰', name: 'rabbit face', keywords: ['rabbit', 'bunny'] },
      { char: '🦊', name: 'fox', keywords: ['fox'] },
      { char: '🐻', name: 'bear', keywords: ['bear'] },
      { char: '🐼', name: 'panda', keywords: ['panda', 'bear'] },
      { char: '🐨', name: 'koala', keywords: ['koala'] },
      { char: '🐯', name: 'tiger face', keywords: ['tiger'] },
      { char: '🦁', name: 'lion', keywords: ['lion'] },
      { char: '🐮', name: 'cow face', keywords: ['cow'] },
      { char: '🐷', name: 'pig face', keywords: ['pig'] },
      { char: '🐸', name: 'frog', keywords: ['frog', 'toad'] },
      { char: '🐵', name: 'monkey face', keywords: ['monkey'] },
      { char: '🙈', name: 'see-no-evil monkey', keywords: ['monkey', 'shy', 'hide'] },
      { char: '🐔', name: 'chicken', keywords: ['chicken', 'hen'] },
      { char: '🐧', name: 'penguin', keywords: ['penguin'] },
      { char: '🐦', name: 'bird', keywords: ['bird'] },
      { char: '🦆', name: 'duck', keywords: ['duck'] },
      { char: '🦉', name: 'owl', keywords: ['owl'] },
      { char: '🦄', name: 'unicorn', keywords: ['unicorn', 'magic'] },
      { char: '🐝', name: 'honeybee', keywords: ['bee', 'buzz'] },
      { char: '🦋', name: 'butterfly', keywords: ['butterfly'] },
      { char: '🐌', name: 'snail', keywords: ['snail', 'slow'] },
      { char: '🐞', name: 'lady beetle', keywords: ['ladybug', 'bug'] },
      { char: '🐢', name: 'turtle', keywords: ['turtle', 'slow'] },
      { char: '🐍', name: 'snake', keywords: ['snake'] },
      { char: '🐙', name: 'octopus', keywords: ['octopus'] },
      { char: '🐠', name: 'tropical fish', keywords: ['fish'] },
      { char: '🐬', name: 'dolphin', keywords: ['dolphin'] },
      { char: '🐳', name: 'spouting whale', keywords: ['whale'] },
      { char: '🦈', name: 'shark', keywords: ['shark'] },
      { char: '🐊', name: 'crocodile', keywords: ['crocodile', 'alligator'] },
      { char: '🐘', name: 'elephant', keywords: ['elephant'] },
      { char: '🦒', name: 'giraffe', keywords: ['giraffe'] },
      { char: '🐴', name: 'horse face', keywords: ['horse'] },
      { char: '🌵', name: 'cactus', keywords: ['cactus', 'plant'] },
      { char: '🌲', name: 'evergreen tree', keywords: ['tree', 'pine'] },
      { char: '🌳', name: 'deciduous tree', keywords: ['tree'] },
      { char: '🌴', name: 'palm tree', keywords: ['tree', 'palm', 'beach'] },
      { char: '🌱', name: 'seedling', keywords: ['plant', 'grow', 'sprout'] },
      { char: '🌿', name: 'herb', keywords: ['plant', 'leaf', 'herb'] },
      { char: '🍀', name: 'four leaf clover', keywords: ['luck', 'clover'] },
      { char: '🍁', name: 'maple leaf', keywords: ['leaf', 'autumn', 'fall'] },
      { char: '🍂', name: 'fallen leaf', keywords: ['leaf', 'autumn'] },
      { char: '🌸', name: 'cherry blossom', keywords: ['flower', 'blossom', 'spring'] },
      { char: '🌼', name: 'blossom', keywords: ['flower'] },
      { char: '🌻', name: 'sunflower', keywords: ['flower', 'sunflower'] },
      { char: '🌹', name: 'rose', keywords: ['flower', 'rose', 'love'] },
      { char: '🌺', name: 'hibiscus', keywords: ['flower'] },
      { char: '🌷', name: 'tulip', keywords: ['flower', 'tulip'] },
      { char: '🌍', name: 'globe showing Europe-Africa', keywords: ['earth', 'world', 'globe'] },
      { char: '🌙', name: 'crescent moon', keywords: ['moon', 'night'] },
      { char: '⭐', name: 'star', keywords: ['star', 'favorite'] },
      { char: '🌟', name: 'glowing star', keywords: ['star', 'shine', 'sparkle'] },
      { char: '✨', name: 'sparkles', keywords: ['sparkle', 'shine', 'magic', 'clean'] },
      { char: '⚡', name: 'high voltage', keywords: ['lightning', 'electric', 'fast'] },
      { char: '🔥', name: 'fire', keywords: ['fire', 'hot', 'lit', 'flame'] },
      { char: '🌈', name: 'rainbow', keywords: ['rainbow', 'pride'] },
      { char: '☀️', name: 'sun', keywords: ['sun', 'sunny', 'weather'] },
      { char: '⛅', name: 'sun behind cloud', keywords: ['cloud', 'weather'] },
      { char: '☁️', name: 'cloud', keywords: ['cloud', 'weather'] },
      { char: '❄️', name: 'snowflake', keywords: ['snow', 'cold', 'winter'] },
      { char: '⛄', name: 'snowman', keywords: ['snow', 'winter'] },
      { char: '💧', name: 'droplet', keywords: ['water', 'drop'] },
      { char: '🌊', name: 'water wave', keywords: ['wave', 'ocean', 'sea'] },
    ],
  },
  {
    id: 'food',
    label: 'Food & Drink',
    emojis: [
      { char: '🍎', name: 'red apple', keywords: ['apple', 'fruit'] },
      { char: '🍊', name: 'tangerine', keywords: ['orange', 'fruit'] },
      { char: '🍋', name: 'lemon', keywords: ['lemon', 'fruit', 'sour'] },
      { char: '🍌', name: 'banana', keywords: ['banana', 'fruit'] },
      { char: '🍉', name: 'watermelon', keywords: ['watermelon', 'fruit'] },
      { char: '🍇', name: 'grapes', keywords: ['grapes', 'fruit', 'wine'] },
      { char: '🍓', name: 'strawberry', keywords: ['strawberry', 'fruit'] },
      { char: '🫐', name: 'blueberries', keywords: ['blueberry', 'fruit'] },
      { char: '🍒', name: 'cherries', keywords: ['cherry', 'fruit'] },
      { char: '🍑', name: 'peach', keywords: ['peach', 'fruit'] },
      { char: '🥭', name: 'mango', keywords: ['mango', 'fruit'] },
      { char: '🍍', name: 'pineapple', keywords: ['pineapple', 'fruit'] },
      { char: '🥥', name: 'coconut', keywords: ['coconut'] },
      { char: '🥑', name: 'avocado', keywords: ['avocado'] },
      { char: '🍅', name: 'tomato', keywords: ['tomato'] },
      { char: '🥕', name: 'carrot', keywords: ['carrot', 'vegetable'] },
      { char: '🌽', name: 'ear of corn', keywords: ['corn'] },
      { char: '🌶️', name: 'hot pepper', keywords: ['pepper', 'spicy', 'chili'] },
      { char: '🥦', name: 'broccoli', keywords: ['broccoli', 'vegetable'] },
      { char: '🍄', name: 'mushroom', keywords: ['mushroom'] },
      { char: '🥐', name: 'croissant', keywords: ['croissant', 'bread'] },
      { char: '🍞', name: 'bread', keywords: ['bread', 'toast'] },
      { char: '🥖', name: 'baguette bread', keywords: ['bread', 'baguette'] },
      { char: '🧀', name: 'cheese wedge', keywords: ['cheese'] },
      { char: '🥚', name: 'egg', keywords: ['egg'] },
      { char: '🍳', name: 'cooking', keywords: ['egg', 'breakfast', 'fried'] },
      { char: '🥞', name: 'pancakes', keywords: ['pancakes', 'breakfast'] },
      { char: '🥓', name: 'bacon', keywords: ['bacon', 'breakfast'] },
      { char: '🍔', name: 'hamburger', keywords: ['burger', 'food'] },
      { char: '🍟', name: 'french fries', keywords: ['fries', 'food'] },
      { char: '🍕', name: 'pizza', keywords: ['pizza', 'food'] },
      { char: '🌭', name: 'hot dog', keywords: ['hotdog', 'food'] },
      { char: '🌮', name: 'taco', keywords: ['taco', 'food'] },
      { char: '🌯', name: 'burrito', keywords: ['burrito', 'food'] },
      { char: '🍜', name: 'steaming bowl', keywords: ['noodles', 'ramen', 'soup'] },
      { char: '🍝', name: 'spaghetti', keywords: ['pasta', 'spaghetti'] },
      { char: '🍣', name: 'sushi', keywords: ['sushi', 'food'] },
      { char: '🍱', name: 'bento box', keywords: ['bento', 'food'] },
      { char: '🍚', name: 'cooked rice', keywords: ['rice'] },
      { char: '🍦', name: 'soft ice cream', keywords: ['ice cream', 'dessert'] },
      { char: '🍩', name: 'doughnut', keywords: ['donut', 'dessert'] },
      { char: '🍪', name: 'cookie', keywords: ['cookie', 'dessert'] },
      { char: '🎂', name: 'birthday cake', keywords: ['cake', 'birthday'] },
      { char: '🍰', name: 'shortcake', keywords: ['cake', 'dessert'] },
      { char: '🧁', name: 'cupcake', keywords: ['cupcake', 'dessert'] },
      { char: '🍫', name: 'chocolate bar', keywords: ['chocolate', 'candy'] },
      { char: '🍬', name: 'candy', keywords: ['candy', 'sweet'] },
      { char: '🍭', name: 'lollipop', keywords: ['lollipop', 'candy'] },
      { char: '🍿', name: 'popcorn', keywords: ['popcorn', 'movie'] },
      { char: '☕', name: 'hot beverage', keywords: ['coffee', 'tea', 'drink'] },
      { char: '🍵', name: 'teacup without handle', keywords: ['tea', 'drink'] },
      { char: '🍺', name: 'beer mug', keywords: ['beer', 'drink'] },
      { char: '🍻', name: 'clinking beer mugs', keywords: ['beer', 'cheers', 'drink'] },
      { char: '🍷', name: 'wine glass', keywords: ['wine', 'drink'] },
      { char: '🍸', name: 'cocktail glass', keywords: ['cocktail', 'drink', 'martini'] },
      { char: '🥂', name: 'clinking glasses', keywords: ['cheers', 'celebrate', 'champagne'] },
      { char: '🥤', name: 'cup with straw', keywords: ['soda', 'drink'] },
      { char: '🧊', name: 'ice', keywords: ['ice', 'cold'] },
    ],
  },
  {
    id: 'activities',
    label: 'Activities',
    emojis: [
      { char: '⚽', name: 'soccer ball', keywords: ['soccer', 'football', 'sport'] },
      { char: '🏀', name: 'basketball', keywords: ['basketball', 'sport'] },
      { char: '🏈', name: 'american football', keywords: ['football', 'sport'] },
      { char: '⚾', name: 'baseball', keywords: ['baseball', 'sport'] },
      { char: '🎾', name: 'tennis', keywords: ['tennis', 'sport'] },
      { char: '🏐', name: 'volleyball', keywords: ['volleyball', 'sport'] },
      { char: '🏉', name: 'rugby football', keywords: ['rugby', 'sport'] },
      { char: '🎱', name: 'pool 8 ball', keywords: ['pool', 'billiards'] },
      { char: '🏓', name: 'ping pong', keywords: ['table tennis', 'ping pong'] },
      { char: '🏸', name: 'badminton', keywords: ['badminton', 'sport'] },
      { char: '🥅', name: 'goal net', keywords: ['goal', 'sport'] },
      { char: '⛳', name: 'flag in hole', keywords: ['golf', 'sport'] },
      { char: '🏹', name: 'bow and arrow', keywords: ['archery', 'arrow'] },
      { char: '🎣', name: 'fishing pole', keywords: ['fishing'] },
      { char: '🥊', name: 'boxing glove', keywords: ['boxing', 'fight'] },
      { char: '🥋', name: 'martial arts uniform', keywords: ['karate', 'judo'] },
      { char: '⛸️', name: 'ice skate', keywords: ['skating', 'ice'] },
      { char: '🎿', name: 'skis', keywords: ['ski', 'snow'] },
      { char: '🏂', name: 'snowboarder', keywords: ['snowboard', 'snow'] },
      { char: '🏋️', name: 'person lifting weights', keywords: ['gym', 'lift', 'workout'] },
      { char: '🤸', name: 'person cartwheeling', keywords: ['cartwheel', 'gymnastics'] },
      { char: '🤽', name: 'person playing water polo', keywords: ['water polo'] },
      { char: '🏊', name: 'person swimming', keywords: ['swim', 'swimming'] },
      { char: '🚴', name: 'person biking', keywords: ['bike', 'cycling'] },
      { char: '🏆', name: 'trophy', keywords: ['trophy', 'win', 'award', 'champion'] },
      { char: '🥇', name: 'first place medal', keywords: ['gold', 'win', 'medal'] },
      { char: '🥈', name: 'second place medal', keywords: ['silver', 'medal'] },
      { char: '🥉', name: 'third place medal', keywords: ['bronze', 'medal'] },
      { char: '🏅', name: 'sports medal', keywords: ['medal', 'award'] },
      { char: '🎖️', name: 'military medal', keywords: ['medal', 'honor'] },
      { char: '🎯', name: 'bullseye', keywords: ['target', 'dart', 'goal'] },
      { char: '🎮', name: 'video game', keywords: ['game', 'gaming', 'controller'] },
      { char: '🕹️', name: 'joystick', keywords: ['game', 'arcade'] },
      { char: '🎲', name: 'game die', keywords: ['dice', 'game'] },
      { char: '🎰', name: 'slot machine', keywords: ['slots', 'gamble', 'casino'] },
      { char: '🎨', name: 'artist palette', keywords: ['art', 'paint', 'creative'] },
      { char: '🎭', name: 'performing arts', keywords: ['theater', 'drama', 'masks'] },
      { char: '🎤', name: 'microphone', keywords: ['mic', 'sing', 'karaoke'] },
      { char: '🎧', name: 'headphone', keywords: ['music', 'headphones', 'audio'] },
      { char: '🎵', name: 'musical note', keywords: ['music', 'note'] },
      { char: '🎶', name: 'musical notes', keywords: ['music', 'notes'] },
      { char: '🎸', name: 'guitar', keywords: ['guitar', 'music'] },
      { char: '🎹', name: 'musical keyboard', keywords: ['piano', 'music'] },
      { char: '🥁', name: 'drum', keywords: ['drum', 'music'] },
      { char: '🎺', name: 'trumpet', keywords: ['trumpet', 'music'] },
      { char: '🎬', name: 'clapper board', keywords: ['movie', 'film', 'action'] },
      { char: '🎉', name: 'party popper', keywords: ['party', 'celebrate', 'congrats', 'tada'] },
      { char: '🎊', name: 'confetti ball', keywords: ['party', 'celebrate', 'confetti'] },
      { char: '🎈', name: 'balloon', keywords: ['balloon', 'party'] },
      { char: '🎁', name: 'wrapped gift', keywords: ['gift', 'present', 'birthday'] },
    ],
  },
  {
    id: 'travel',
    label: 'Travel & Places',
    emojis: [
      { char: '🚗', name: 'automobile', keywords: ['car', 'drive'] },
      { char: '🚕', name: 'taxi', keywords: ['taxi', 'cab'] },
      { char: '🚙', name: 'sport utility vehicle', keywords: ['suv', 'car'] },
      { char: '🚌', name: 'bus', keywords: ['bus'] },
      { char: '🏎️', name: 'racing car', keywords: ['race', 'car', 'fast'] },
      { char: '🚓', name: 'police car', keywords: ['police', 'cop'] },
      { char: '🚑', name: 'ambulance', keywords: ['ambulance', 'emergency'] },
      { char: '🚒', name: 'fire engine', keywords: ['fire truck'] },
      { char: '🚜', name: 'tractor', keywords: ['tractor', 'farm'] },
      { char: '🛵', name: 'motor scooter', keywords: ['scooter'] },
      { char: '🏍️', name: 'motorcycle', keywords: ['motorcycle', 'bike'] },
      { char: '🚲', name: 'bicycle', keywords: ['bike', 'bicycle'] },
      { char: '🚂', name: 'locomotive', keywords: ['train'] },
      { char: '✈️', name: 'airplane', keywords: ['plane', 'flight', 'travel'] },
      { char: '🚀', name: 'rocket', keywords: ['rocket', 'launch', 'space', 'fast'] },
      { char: '🛸', name: 'flying saucer', keywords: ['ufo', 'alien'] },
      { char: '🚁', name: 'helicopter', keywords: ['helicopter'] },
      { char: '⛵', name: 'sailboat', keywords: ['boat', 'sail'] },
      { char: '🚤', name: 'speedboat', keywords: ['boat', 'speed'] },
      { char: '🛳️', name: 'passenger ship', keywords: ['cruise', 'ship'] },
      { char: '⚓', name: 'anchor', keywords: ['anchor', 'boat'] },
      { char: '🚦', name: 'vertical traffic light', keywords: ['traffic', 'light'] },
      { char: '🗺️', name: 'world map', keywords: ['map', 'travel'] },
      { char: '🗽', name: 'Statue of Liberty', keywords: ['liberty', 'new york'] },
      { char: '🗼', name: 'Tokyo tower', keywords: ['tokyo', 'tower'] },
      { char: '🏰', name: 'castle', keywords: ['castle'] },
      { char: '🏯', name: 'Japanese castle', keywords: ['castle', 'japan'] },
      { char: '🏟️', name: 'stadium', keywords: ['stadium', 'arena'] },
      { char: '🎡', name: 'ferris wheel', keywords: ['ferris wheel', 'fair'] },
      { char: '🎢', name: 'roller coaster', keywords: ['roller coaster', 'amusement'] },
      { char: '🏖️', name: 'beach with umbrella', keywords: ['beach', 'vacation'] },
      { char: '🏝️', name: 'desert island', keywords: ['island', 'beach'] },
      { char: '⛰️', name: 'mountain', keywords: ['mountain'] },
      { char: '🌋', name: 'volcano', keywords: ['volcano'] },
      { char: '🏕️', name: 'camping', keywords: ['camp', 'tent'] },
      { char: '🏠', name: 'house', keywords: ['house', 'home'] },
      { char: '🏢', name: 'office building', keywords: ['office', 'building', 'work'] },
      { char: '🏥', name: 'hospital', keywords: ['hospital'] },
      { char: '🏦', name: 'bank', keywords: ['bank'] },
      { char: '🏫', name: 'school', keywords: ['school'] },
      { char: '⛲', name: 'fountain', keywords: ['fountain'] },
      { char: '🌉', name: 'bridge at night', keywords: ['bridge'] },
      { char: '🎇', name: 'sparkler', keywords: ['fireworks', 'sparkler'] },
      { char: '🎆', name: 'fireworks', keywords: ['fireworks', 'celebrate'] },
    ],
  },
  {
    id: 'objects',
    label: 'Objects',
    emojis: [
      { char: '⌚', name: 'watch', keywords: ['watch', 'time'] },
      { char: '📱', name: 'mobile phone', keywords: ['phone', 'mobile', 'cell'] },
      { char: '💻', name: 'laptop', keywords: ['laptop', 'computer'] },
      { char: '⌨️', name: 'keyboard', keywords: ['keyboard', 'type'] },
      { char: '🖥️', name: 'desktop computer', keywords: ['computer', 'desktop'] },
      { char: '🖨️', name: 'printer', keywords: ['printer', 'print'] },
      { char: '🖱️', name: 'computer mouse', keywords: ['mouse', 'click'] },
      { char: '💾', name: 'floppy disk', keywords: ['save', 'disk'] },
      { char: '💿', name: 'optical disk', keywords: ['cd', 'disk'] },
      { char: '📷', name: 'camera', keywords: ['camera', 'photo'] },
      { char: '📸', name: 'camera with flash', keywords: ['camera', 'photo', 'flash'] },
      { char: '📹', name: 'video camera', keywords: ['video', 'camera'] },
      { char: '🎥', name: 'movie camera', keywords: ['movie', 'film', 'camera'] },
      { char: '📞', name: 'telephone receiver', keywords: ['phone', 'call'] },
      { char: '📺', name: 'television', keywords: ['tv', 'television'] },
      { char: '📻', name: 'radio', keywords: ['radio'] },
      { char: '🔋', name: 'battery', keywords: ['battery', 'power'] },
      { char: '🔌', name: 'electric plug', keywords: ['plug', 'power'] },
      { char: '💡', name: 'light bulb', keywords: ['idea', 'bulb', 'light'] },
      { char: '🔦', name: 'flashlight', keywords: ['flashlight', 'torch'] },
      { char: '🕯️', name: 'candle', keywords: ['candle'] },
      { char: '💰', name: 'money bag', keywords: ['money', 'cash', 'rich'] },
      { char: '💵', name: 'dollar banknote', keywords: ['money', 'dollar', 'cash'] },
      { char: '💳', name: 'credit card', keywords: ['card', 'pay', 'money'] },
      { char: '💎', name: 'gem stone', keywords: ['diamond', 'gem', 'jewel'] },
      { char: '⚖️', name: 'balance scale', keywords: ['justice', 'law', 'balance'] },
      { char: '🔧', name: 'wrench', keywords: ['tool', 'wrench', 'fix'] },
      { char: '🔨', name: 'hammer', keywords: ['hammer', 'tool', 'build'] },
      { char: '🛠️', name: 'hammer and wrench', keywords: ['tools', 'fix', 'build'] },
      { char: '⚙️', name: 'gear', keywords: ['gear', 'settings', 'cog'] },
      { char: '🔩', name: 'nut and bolt', keywords: ['bolt', 'nut', 'tool'] },
      { char: '🧰', name: 'toolbox', keywords: ['toolbox', 'tools'] },
      { char: '🧲', name: 'magnet', keywords: ['magnet'] },
      { char: '🔬', name: 'microscope', keywords: ['science', 'microscope'] },
      { char: '🔭', name: 'telescope', keywords: ['telescope', 'space'] },
      { char: '💊', name: 'pill', keywords: ['pill', 'medicine'] },
      { char: '💉', name: 'syringe', keywords: ['shot', 'vaccine', 'needle'] },
      { char: '🔑', name: 'key', keywords: ['key', 'unlock'] },
      { char: '🔒', name: 'locked', keywords: ['lock', 'secure', 'private'] },
      { char: '🔓', name: 'unlocked', keywords: ['unlock', 'open'] },
      { char: '🔔', name: 'bell', keywords: ['bell', 'notification', 'alert'] },
      { char: '📌', name: 'pushpin', keywords: ['pin', 'pinned'] },
      { char: '📎', name: 'paperclip', keywords: ['paperclip', 'attach'] },
      { char: '✂️', name: 'scissors', keywords: ['scissors', 'cut'] },
      { char: '📝', name: 'memo', keywords: ['note', 'memo', 'write'] },
      { char: '📅', name: 'calendar', keywords: ['calendar', 'date'] },
      { char: '📈', name: 'chart increasing', keywords: ['chart', 'growth', 'up', 'stats'] },
      { char: '📉', name: 'chart decreasing', keywords: ['chart', 'down', 'loss'] },
      { char: '📊', name: 'bar chart', keywords: ['chart', 'stats', 'graph'] },
      { char: '📚', name: 'books', keywords: ['books', 'read', 'study'] },
      { char: '📖', name: 'open book', keywords: ['book', 'read'] },
      { char: '✏️', name: 'pencil', keywords: ['pencil', 'write', 'edit'] },
      { char: '🖊️', name: 'pen', keywords: ['pen', 'write'] },
      { char: '🔍', name: 'magnifying glass tilted left', keywords: ['search', 'find', 'zoom'] },
      { char: '🧪', name: 'test tube', keywords: ['science', 'lab', 'chemistry'] },
      { char: '🧬', name: 'dna', keywords: ['dna', 'science', 'biology'] },
    ],
  },
  {
    id: 'symbols',
    label: 'Symbols',
    emojis: [
      { char: '✅', name: 'check mark button', keywords: ['check', 'done', 'yes', 'ok'] },
      { char: '✔️', name: 'check mark', keywords: ['check', 'done', 'tick'] },
      { char: '❌', name: 'cross mark', keywords: ['x', 'no', 'wrong', 'cancel'] },
      { char: '❎', name: 'cross mark button', keywords: ['x', 'no'] },
      { char: '➕', name: 'plus', keywords: ['plus', 'add'] },
      { char: '➖', name: 'minus', keywords: ['minus', 'subtract'] },
      { char: '➗', name: 'divide', keywords: ['divide'] },
      { char: '✖️', name: 'multiply', keywords: ['multiply', 'times'] },
      { char: '❓', name: 'question mark', keywords: ['question', 'help'] },
      { char: '❗', name: 'exclamation mark', keywords: ['exclamation', 'important'] },
      { char: '⚠️', name: 'warning', keywords: ['warning', 'caution', 'alert'] },
      { char: '🚫', name: 'prohibited', keywords: ['no', 'forbidden', 'stop'] },
      { char: '✳️', name: 'eight-spoked asterisk', keywords: ['asterisk', 'star'] },
      { char: '🔆', name: 'bright button', keywords: ['bright', 'brightness'] },
      { char: '⭕', name: 'hollow red circle', keywords: ['circle', 'o'] },
      { char: '💲', name: 'heavy dollar sign', keywords: ['dollar', 'money'] },
      { char: '⚡', name: 'high voltage symbol', keywords: ['power', 'lightning'] },
      { char: '♻️', name: 'recycling symbol', keywords: ['recycle', 'green'] },
      { char: '🔱', name: 'trident emblem', keywords: ['trident'] },
      { char: '⚜️', name: 'fleur-de-lis', keywords: ['fleur'] },
      { char: '🔰', name: 'Japanese symbol for beginner', keywords: ['beginner'] },
      { char: '✅', name: 'check', keywords: ['confirm'] },
      { char: '🆗', name: 'OK button', keywords: ['ok', 'okay'] },
      { char: '🆕', name: 'NEW button', keywords: ['new'] },
      { char: '🆓', name: 'FREE button', keywords: ['free'] },
      { char: '🔝', name: 'TOP arrow', keywords: ['top', 'up'] },
      { char: '🔙', name: 'BACK arrow', keywords: ['back'] },
      { char: '⬆️', name: 'up arrow', keywords: ['up', 'arrow'] },
      { char: '⬇️', name: 'down arrow', keywords: ['down', 'arrow'] },
      { char: '⬅️', name: 'left arrow', keywords: ['left', 'arrow'] },
      { char: '➡️', name: 'right arrow', keywords: ['right', 'arrow'] },
      { char: '🔄', name: 'counterclockwise arrows', keywords: ['refresh', 'reload', 'sync'] },
      { char: '▶️', name: 'play button', keywords: ['play'] },
      { char: '⏸️', name: 'pause button', keywords: ['pause'] },
      { char: '⏹️', name: 'stop button', keywords: ['stop'] },
      { char: '🔀', name: 'shuffle tracks button', keywords: ['shuffle', 'random'] },
      { char: '➰', name: 'curly loop', keywords: ['loop'] },
      { char: '〽️', name: 'part alternation mark', keywords: ['mark'] },
      { char: '©️', name: 'copyright', keywords: ['copyright'] },
      { char: '®️', name: 'registered', keywords: ['registered', 'trademark'] },
      { char: '™️', name: 'trade mark', keywords: ['trademark'] },
      { char: '🔟', name: 'keycap 10', keywords: ['ten', '10'] },
      { char: '#️⃣', name: 'keycap number sign', keywords: ['hash', 'number'] },
      { char: '🅰️', name: 'A button blood type', keywords: ['a'] },
      { char: '🆎', name: 'AB button blood type', keywords: ['ab'] },
      { char: '🔴', name: 'red circle', keywords: ['red', 'circle', 'dot'] },
      { char: '🟠', name: 'orange circle', keywords: ['orange', 'circle'] },
      { char: '🟡', name: 'yellow circle', keywords: ['yellow', 'circle'] },
      { char: '🟢', name: 'green circle', keywords: ['green', 'circle', 'online'] },
      { char: '🔵', name: 'blue circle', keywords: ['blue', 'circle'] },
      { char: '⚫', name: 'black circle', keywords: ['black', 'circle'] },
      { char: '⚪', name: 'white circle', keywords: ['white', 'circle'] },
      { char: '🟥', name: 'red square', keywords: ['red', 'square'] },
      { char: '🟩', name: 'green square', keywords: ['green', 'square'] },
      { char: '🟦', name: 'blue square', keywords: ['blue', 'square'] },
    ],
  },
  {
    id: 'flags',
    label: 'Flags',
    emojis: [
      { char: '🏁', name: 'chequered flag', keywords: ['race', 'finish', 'flag'] },
      { char: '🚩', name: 'triangular flag', keywords: ['flag', 'red flag'] },
      { char: '🎌', name: 'crossed flags', keywords: ['flags', 'japan'] },
      { char: '🏴', name: 'black flag', keywords: ['flag', 'black'] },
      { char: '🏳️', name: 'white flag', keywords: ['flag', 'surrender', 'white'] },
      { char: '🏳️‍🌈', name: 'rainbow flag', keywords: ['pride', 'rainbow', 'lgbt'] },
      { char: '🇺🇸', name: 'flag United States', keywords: ['usa', 'america', 'us'] },
      { char: '🇬🇧', name: 'flag United Kingdom', keywords: ['uk', 'britain', 'england'] },
      { char: '🇨🇦', name: 'flag Canada', keywords: ['canada'] },
      { char: '🇦🇺', name: 'flag Australia', keywords: ['australia'] },
      { char: '🇵🇭', name: 'flag Philippines', keywords: ['philippines', 'ph'] },
      { char: '🇯🇵', name: 'flag Japan', keywords: ['japan', 'jp'] },
      { char: '🇩🇪', name: 'flag Germany', keywords: ['germany', 'de'] },
      { char: '🇫🇷', name: 'flag France', keywords: ['france', 'fr'] },
      { char: '🇮🇳', name: 'flag India', keywords: ['india', 'in'] },
      { char: '🇧🇷', name: 'flag Brazil', keywords: ['brazil', 'br'] },
      { char: '🇲🇽', name: 'flag Mexico', keywords: ['mexico', 'mx'] },
      { char: '🇪🇸', name: 'flag Spain', keywords: ['spain', 'es'] },
      { char: '🇮🇹', name: 'flag Italy', keywords: ['italy', 'it'] },
      { char: '🇨🇳', name: 'flag China', keywords: ['china', 'cn'] },
      { char: '🇰🇷', name: 'flag South Korea', keywords: ['korea', 'kr'] },
    ],
  },
]

/**
 * Case-insensitive substring search across emoji name + keywords.
 * Empty / whitespace-only query returns [] (the picker shows category
 * sections instead). Results are de-duplicated by char.
 */
export function searchEmojis(query) {
  const q = (query || '').trim().toLowerCase()
  if (!q) return []
  const seen = new Set()
  const out = []
  for (const cat of EMOJI_CATEGORIES) {
    for (const e of cat.emojis) {
      if (seen.has(e.char)) continue
      const hit =
        e.name.toLowerCase().includes(q) ||
        e.keywords.some(k => k.toLowerCase().includes(q))
      if (hit) {
        seen.add(e.char)
        out.push(e)
      }
    }
  }
  return out
}
